import { AfterViewChecked, AfterViewInit, Directive, ElementRef, NgZone, OnDestroy, Renderer2 } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

const CLEAR_ICON = '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">'
  + '<path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
const BUTTON_SIZE = 20;
const BUTTON_INSET = 6;

/**
 * Same clear (×) button on every `<input type="search">`, in every browser: Chromium/Safari draw
 * their own and Firefox draws none, so the native one is hidden (`.has-search-clear` in
 * styles.scss) and this one is added as a sibling of the input.
 *
 * Deliberately a directive and not a wrapper component: the input stays in the host template, so
 * every page keeps its own (view-encapsulated) input styles and layout untouched. Clearing sets the
 * value and dispatches a native `input` event, exactly like the browser's own button, so both
 * `(input)` handlers and `ngModel` pick it up with no per-page code.
 *
 * Picked up just by importing it in a standalone component: the selector matches the attribute.
 */
@Directive({
  selector: 'input[type=search]',
  standalone: true
})
export class SearchClearDirective implements AfterViewInit, AfterViewChecked, OnDestroy {
  private button?: HTMLButtonElement;
  private visible = false;
  private resizeObserver?: ResizeObserver;
  private unlisteners: Array<() => void> = [];

  constructor(
    private el: ElementRef<HTMLInputElement>,
    private renderer: Renderer2,
    private zone: NgZone,
    private translate: TranslateService
  ) {}

  ngAfterViewInit(): void {
    const input = this.el.nativeElement;
    const parent = input.parentElement;
    if (!parent || typeof window === 'undefined') {
      return;
    }
    this.renderer.addClass(input, 'has-search-clear');

    // Anchor the button to the input's parent, so it moves with the input when content above it
    // changes height (charts loading, banners...) instead of with some far-away ancestor.
    if (getComputedStyle(parent).position === 'static') {
      this.renderer.setStyle(parent, 'position', 'relative');
    }

    const button: HTMLButtonElement = this.renderer.createElement('button');
    this.renderer.setAttribute(button, 'type', 'button');
    this.renderer.setAttribute(button, 'tabindex', '-1');
    this.renderer.addClass(button, 'search-clear-btn');
    this.renderer.setProperty(button, 'innerHTML', CLEAR_ICON);
    this.renderer.setStyle(button, 'display', 'none');
    this.renderer.insertBefore(parent, button, input.nextSibling);
    this.button = button;
    this.updateLabel();

    const langSub = this.translate.onLangChange.subscribe(() => this.updateLabel());
    this.unlisteners.push(
      // Keep focus in the input, so the user can type again right away.
      this.renderer.listen(button, 'mousedown', (event: MouseEvent) => event.preventDefault()),
      this.renderer.listen(button, 'click', () => this.clear()),
      this.renderer.listen(input, 'input', () => this.sync()),
      () => langSub.unsubscribe()
    );

    this.zone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => this.position());
      this.resizeObserver.observe(input);
      this.resizeObserver.observe(parent);
    });

    this.sync();
  }

  /**
   * Values written programmatically ([value], ngModel, a restored search) don't fire `input`.
   * After-view rather than DoCheck: DoCheck runs before the host's [value] binding is applied.
   */
  ngAfterViewChecked(): void {
    this.sync();
  }

  ngOnDestroy(): void {
    this.unlisteners.forEach(unlisten => unlisten());
    this.resizeObserver?.disconnect();
    this.button?.remove();
  }

  private clear(): void {
    const input = this.el.nativeElement;
    input.value = '';
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.focus();
    this.sync();
  }

  private sync(): void {
    if (!this.button) {
      return;
    }
    const input = this.el.nativeElement;
    const shouldShow = !!input.value && !input.disabled && !input.readOnly;
    if (shouldShow === this.visible) {
      return;
    }
    this.visible = shouldShow;
    this.renderer.setStyle(this.button, 'display', shouldShow ? 'inline-flex' : 'none');
    if (shouldShow) {
      this.position();
    }
  }

  /**
   * Measured rather than pure CSS: the input keeps its page layout (no wrapper), so the button is
   * placed over the input's right edge relative to whatever its containing block turns out to be.
   */
  private position(): void {
    const button = this.button;
    if (!button || !this.visible) {
      return;
    }
    const input = this.el.nativeElement;
    button.style.left = '0px';
    button.style.top = '0px';
    const origin = button.getBoundingClientRect();
    const rect = input.getBoundingClientRect();
    button.style.left = `${rect.right - origin.left - BUTTON_SIZE - BUTTON_INSET}px`;
    button.style.top = `${rect.top - origin.top + (rect.height - BUTTON_SIZE) / 2}px`;
  }

  private updateLabel(): void {
    if (this.button) {
      const label = this.translate.instant('common.clearSearch');
      this.renderer.setAttribute(this.button, 'aria-label', label);
      this.renderer.setAttribute(this.button, 'title', label);
    }
  }
}
